import posthog from "posthog-js"

/**
 * OKS-533 — Client-side PostHog boundary (product analytics foundation).
 *
 * Mirrors the New Relic navigate hook (`./newrelic-navigate.ts`): a small,
 * unit-testable module that the Inertia entrypoint (`./inertia.tsx`) wires
 * into boot + SPA navigation. It reads ONLY the dedicated, PII-minimal
 * `posthog` shared prop — never `currentUser` — so email / display name /
 * legal name have no path into analytics (TRD-OKS-533 KD-2).
 *
 * GUARD (mandatory): every export no-ops when the bootstrap config is
 * absent (`null`/`undefined`) — i.e. no public key configured, or no
 * authenticated user. In dev / CI / test / un-provisioned prod the prop is
 * `null`, so `posthog.init` is never called and no network request is made
 * (behaviour-neutral, mirrors the NR guard posture).
 *
 * Consent-exempt config (KD-6): in-memory (cookieless) persistence,
 * `identified_only` person profiles (anonymous surfaces create nothing),
 * and autocapture / pageview / pageleave / session-recording all OFF —
 * this ticket is foundation-only (identify + group), events land in
 * OKS-534. IP masking is enforced PROJECT-side, not here (KD-6a).
 */

/**
 * The minimal bootstrap shape carried by the `posthog` Inertia shared prop.
 * ids are strings so they match the server-side `group_key`
 * (`Analytics::PostHog.group_identify`).
 */
export interface PostHogBootstrap {
  key: string
  host: string
  distinct_id: string
  username: string | null
  brand: { id: string; name: string } | null
}

// Module-local state. `initialized` makes `initPostHog` idempotent across
// SPA navigations. The `last*` markers implement the anti-storm guard
// (KD-7): re-`identify` / re-`group` ONLY when the identity actually
// changes (sign-in, sign-out, admin act-as brand switch), not on every nav.
let initialized = false
let lastDistinctId: string | null = null
let lastBrandId: string | null = null

/**
 * The consent-exempt init options (KD-6). Extracted so the unit spec can
 * assert the exact set without booting the app.
 */
export const CONSENT_EXEMPT_CONFIG = {
  persistence: "memory",
  person_profiles: "identified_only",
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  disable_session_recording: true,
  cross_subdomain_cookie: false,
} as const

/**
 * Initialise posthog-js once from the bootstrap config, then apply the
 * initial identity. No-op when `config` is absent (no key / no user) or
 * when already initialised (idempotent across navigations).
 */
export function initPostHog(config: PostHogBootstrap | null | undefined): void {
  if (!config?.key) return
  if (initialized) return

  posthog.init(config.key, {
    api_host: config.host,
    ...CONSENT_EXEMPT_CONFIG,
  })
  initialized = true

  // Apply the first identity immediately so attribution is live on the
  // page that carried the key (no wait for the next navigation).
  syncPostHogIdentity(config)
}

/**
 * Reconcile the identified user + brand group with the current config.
 * No-op when not initialised or when `config` is absent. Fires
 * `identify` / `group` ONLY on an actual change (anti-storm, KD-7).
 */
export function syncPostHogIdentity(
  config: PostHogBootstrap | null | undefined,
): void {
  if (!initialized || !config) return

  // OKS-534 — guard against a config without a distinct_id (e.g. an
  // anonymous bootstrap shape): never `identify(undefined)`.
  if (config.distinct_id && config.distinct_id !== lastDistinctId) {
    posthog.identify(
      config.distinct_id,
      config.username ? { username: config.username } : {},
    )
    lastDistinctId = config.distinct_id
    // Force a group re-association after an identity change so the new
    // person is linked to their brand even if the brand id is unchanged.
    lastBrandId = null
  }

  const brandId = config.brand?.id ?? null
  if (brandId !== lastBrandId) {
    if (config.brand) posthog.group("brand", config.brand.id)
    lastBrandId = brandId
  }
}

/**
 * OKS-534 — Minimal PRE-AUTH bootstrap for the one client event
 * (`signup_started`). The signup page is unauthenticated, so it carries no
 * `distinct_id`/`brand` — just the public key + host (or null when no key).
 */
export interface AnonymousPostHogBootstrap {
  key: string
  host: string
}

/**
 * OKS-534 — Initialise posthog-js ANONYMOUSLY on a pre-auth surface (the
 * invite signup page) so the pre-identify `signup_started` intent event can
 * fire. No `identify` is called — under `person_profiles: identified_only`
 * the anonymous event creates no profile until a later authenticated
 * `identify` lands. No-op without a key (dev / CI / test / un-provisioned
 * prod → behaviour-neutral) or when already initialised (idempotent; if the
 * app later boots an authenticated identity, `syncPostHogIdentity` upgrades
 * this same instance).
 */
export function initPostHogAnonymous(
  config: AnonymousPostHogBootstrap | null | undefined,
): void {
  if (!config?.key) return
  if (initialized) return

  posthog.init(config.key, {
    api_host: config.host,
    ...CONSENT_EXEMPT_CONFIG,
  })
  initialized = true
}

/**
 * OKS-534 — Capture a product-analytics event client-side. No-op unless
 * posthog-js is initialised (no key / not booted → silent, per UX Spec §3).
 * This is the client counterpart to the server `Analytics::Events` facade;
 * only `signup_started` uses it (all other events are server-fired).
 */
export function capturePostHogEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!initialized) return

  posthog.capture(event, properties)
}

/**
 * Test-only hook to reset module state between cases (the module singletons
 * would otherwise leak `initialized`/`last*` across specs). Not used by the
 * app.
 */
export function __resetPostHogStateForTest(): void {
  initialized = false
  lastDistinctId = null
  lastBrandId = null
}
